import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  Copy, 
  Gift
} from "lucide-react";
import { FaFacebook, FaWhatsapp } from "react-icons/fa";

interface ReferFriendProps {
  competitionId: number;
  title: string;
  description?: string;
}

interface ReferralData {
  referralCode: string;
  referralUrl: string;
  referredCount: number;
}

export default function ReferFriend({ competitionId, title, description }: ReferFriendProps) {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  // Query to get user's referral code and stats
  const { data: referralData } = useQuery<ReferralData>({
    queryKey: ["/api/referral-code"],
    enabled: isAuthenticated,
  });

  const { data: competitionBonusEntries } = useQuery({
    queryKey: [`/api/bonus-entries/${competitionId}`],
    enabled: isAuthenticated,
  });
  // Use uniqueFriendsReferred (count of actual friends) instead of referralEntries (total entry count)
  const competitionReferralCount = competitionBonusEntries?.uniqueFriendsReferred ?? 0;



  const getReferralLink = (social?: string) => {
    if (!referralData?.referralCode) return '';
    
    // Generate URL for the new competition referral route: /ref/competition/:competitionId/:referralCode/:social?
    const baseUrl = window.location.origin;
    const socialParam = social ? `/${social}` : '';
    return `${baseUrl}/ref/competition/${competitionId}/${referralData.referralCode}${socialParam}`;
  };

  const referralLink = getReferralLink();

  const referralMessage = referralLink 
    ? `🎉 Join me in this awesome competition: "${title}"!\n\nWe both get 35 entries each when you sign up using my referral link! 🎁\n\n${referralLink}`
    : '';

  const handleCopyLink = async () => {
    if (!referralMessage) return;

    try {
      await navigator.clipboard.writeText(referralMessage);
      setCopied(true);
      toast({
        title: "Message Copied!",
        description: "Your referral message with link has been copied to clipboard",
      });
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast({
        title: "Copy Failed",
        description: "Please copy the message manually",
        variant: "destructive",
      });
    }
  };

  const handleShare = async (platform: string) => {
    const socialParam = platform === 'facebook' ? 'fb' : platform === 'whatsapp' ? 'wa' : '';
    const trackedReferralLink = getReferralLink(socialParam);
    
    if (!trackedReferralLink) return;

    const trackedReferralMessage = trackedReferralLink 
      ? `🎉 Join me in this awesome competition: "${title}"!\n\nWe both get 35 entries each when you sign up using my referral link! 🎁\n\n${trackedReferralLink}`
      : '';

    let shareUrl = '';

    switch (platform) {
      case "whatsapp":
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(trackedReferralMessage)}`;
        break;
      // Facebook sharing temporarily disabled
      /* case "facebook":
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        
        // Always try to copy message to clipboard first
        try {
          await navigator.clipboard.writeText(trackedReferralMessage);
          toast({
            title: "Message Copied!",
            description: "Share this message in Messenger or Facebook!",
            duration: 4000,
          });
        } catch (err) {
          console.error('Failed to copy message:', err);
        }

        if (isMobile) {
          // Mobile: Try Messenger app first, then Facebook app
          shareUrl = `fb-messenger://share/?link=${encodeURIComponent(trackedReferralLink)}&app_id=${process.env.VITE_FACEBOOK_APP_ID || ''}`;
          
          // Set fallback timeout for Facebook app
          setTimeout(() => {
            window.location.href = `fb://profile`;
            // Final fallback to mobile web
            setTimeout(() => {
              window.open(`https://m.facebook.com/sharer.php?u=${encodeURIComponent(trackedReferralLink)}`, "_blank");
            }, 1000);
          }, 2000);
        } else {
          // Desktop: Try Messenger web first
          shareUrl = `https://www.messenger.com/`;
          
          // Fallback to Facebook sharing dialog
          setTimeout(() => {
            window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(trackedReferralLink)}`, "_blank", "width=600,height=400");
          }, 3000);
        }
        break; */
    }

    if (shareUrl) {
      // Open the sharing window
      const shareWindow = window.open(shareUrl, "_blank", "width=600,height=400,noopener,noreferrer");

      // Automatically track the share without confirmation popup
      try {
        await apiRequest("POST", `/api/competitions/${competitionId}/share`, { 
          platform,
          shareType: 'referral'
        });

        toast({
          title: "Bonus entry added! +1 entry",
          description: `You earned a bonus entry for sharing your referral link on ${platform}!`,
        });
      } catch (error) {
        console.error('Error tracking referral share:', error);
        toast({
          title: "Sharing opened",
          description: `Your ${platform} sharing window has opened. Share to earn bonus entries!`,
        });
      }
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="h-5 w-5 text-primary" />
          Refer a Friend
        </CardTitle>
        <p className="text-sm text-gray-600">
          Share this competition with friends and you both get 35 entries each!
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
          <p className="text-xs text-blue-700">
            <strong>Tip:</strong> Inviting friends is optional, but it's the fastest way to earn bonus entries and share the fun!
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Benefits */}
        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center gap-2">
            <Gift className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">Win Together!</span>
          </div>
          <Badge variant="secondary" className="bg-green-100 text-green-700">
            +35 entries each
          </Badge>
        </div>

        {/* Copy Link and WhatsApp Side by Side */}
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button
              onClick={handleCopyLink}
              disabled={!referralLink}
              variant="outline"
              size="sm"
            >
              <Copy className="h-4 w-4 mr-2" />
              {copied ? "Link Copied!" : "Copy Link"}
            </Button>
            <Button
              onClick={() => handleShare("whatsapp")}
              disabled={!referralLink}
              variant="outline"
              size="sm"
              className="text-green-700 hover:bg-green-100 hover:text-green-800 border-green-200"
            >
              <FaWhatsapp className="h-4 w-4 mr-2" />
              WhatsApp
            </Button>
          </div>
          {/* Facebook sharing temporarily disabled */}
          {/* <Button
            onClick={() => handleShare("facebook")}
            disabled={!referralLink}
            variant="outline"
            size="sm"
            className="text-blue-700 hover:bg-blue-100 hover:text-blue-800 border-blue-200"
          >
            <FaFacebook className="h-4 w-4 mr-2" />
            Facebook
          </Button> */}
        </div>

        {/* Stats - Only show if user has referrals */}
        {competitionReferralCount > 1 && (
          <div className="pt-2 border-t">
            <div className="flex items-center justify-center gap-2 text-green-600">
              <Users className="h-4 w-4" />
              <p className="text-sm font-medium">
                {competitionReferralCount} friends referred!
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
